import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  ClickAwayListener,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { ReactComponent as ProfileIcon } from "../../../../assets/images/profile.svg";
import { ReactComponent as BillingIcon } from "../../../../assets/images/Billing.svg";
import { ReactComponent as MemoryIcon } from "../../../../assets/images/Memory.svg";
import { ReactComponent as LogoutIcon } from "../../../../assets/images/Logout.svg";
import { ReactComponent as HelpIcon } from "../../../../assets/images/Help-Center.svg";
import "./index.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { resetMetaData, setUser } from "../../../../rtkSlices/metaDataSlice";
import { HiOutlineUsers } from "react-icons/hi2";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { useManageSubscriptionQuery } from "../../../../services/user";

type NavProps = {
  name: string;
  role: string;
  profile_picture: string;
};

const UserNav: React.FC<NavProps> = ({ name, role, profile_picture }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const metaData = useSelector((state: any) => state.metaData);
  const userInfo = metaData.userInfo;
  const username = metaData.userInfo["cognito:username"];
  const [managePlan, setManagePlan] = useState(false);

  const manageSubscription = useManageSubscriptionQuery(
    `?username=${username}`,
    { skip: !managePlan }
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationProps, setConfirmationProps] = useState<any>({
    title: "",
    content: "",
    btn1: "",
    btn2: "",
    handleBtn1: () => {},
    handleBtn2: () => {},
    icon: <div></div>,
    tooltip: "",
  });
  const MenuItem = ({
    icon,
    text,
    redirect,
  }: {
    icon: React.ReactNode;
    text: string;
    redirect: () => void;
  }) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      className="nav-item"
      onClick={redirect}
    >
      {icon}
      {text ? (
        <Typography
          variant="body2"
          className="nav-item-text"
          sx={{ color: "white", fontWeight: 500 }}
        >
          {text}
        </Typography>
      ) : (
        <div>
          <CircularProgress
            size={19}
            style={{ color: "grey", margin: "0px 6px" }}
          />
        </div>
      )}
    </Stack>
  );

  React.useEffect(() => {
    if (!manageSubscription.isFetching) {
      if (manageSubscription.isSuccess) {
        if (manageSubscription.data && manageSubscription.data.url) {
          window.location.href = manageSubscription.data.url;
          setManagePlan(false);
        } else {
          setManagePlan(false);
        }
      }
    }
  }, [manageSubscription.isFetching]);

  const logout = () => {
    setShowConfirmation(true);
    const tempConfirmationProps = {
      title: "WARNING",
      bgcolor: "rgba(180, 69, 52, 1)",
      content: `Are you sure you want to logout?`,
      btn1: "Cancel",
      btn2: "Proceed",
      handleBtn1: () => setShowConfirmation(false),
      handleBtn2: () => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("userInfo");
        dispatch(setUser({}));
        dispatch(resetMetaData());
        setShowConfirmation(false);
        navigate("/signin");
      },
      actions: true,
      icon: <div></div>,
    };
    setConfirmationProps(tempConfirmationProps);
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => dispatch(setUser({ nav: false }))}>
        <Card className="nav-card">
          <CardContent sx={{ padding: "0px", paddingBottom: "0px !important" }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              mb={2}
              sx={{ margin: "0px !important" }}
            >
              <Avatar
                sx={{
                  bgcolor: "#333",
                  width: 40,
                  height: 40,
                  color: "white",
                  textTransform: "uppercase",
                }}
                src={profile_picture ? profile_picture : ""}
              >
                {name.split(" ")[0][0] + name.split(" ")[1][0]}
              </Avatar>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ textTransform: "capitalize" }}
              >
                {name}
              </Typography>
            </Stack>

            <Stack spacing={2} sx={{ margin: "5px 0px" }}>
              {MenuItem({
                icon: <ProfileIcon className="nav-item-icon" />,
                text: "Profile",
                redirect: () => {
                  dispatch(setUser({ nav: false }));
                  navigate("/user-profile");
                },
              })}
              {userInfo.subscriptionStatus != "Freemium"
                ? MenuItem({
                    icon: <BillingIcon className="nav-item-icon" />,
                    text: managePlan ? "" : "Billing",
                    redirect: () => {
                      setManagePlan(true);
                    },
                  })
                : null}
              {/* 
              {MenuItem({
                icon: <MemoryIcon className="nav-item-icon" />,
                text: "Memory",
                redirect: () => {},
              })} */}
              {role && role.toLowerCase().includes("admin")
                ? MenuItem({
                    icon: (
                      <HiOutlineUsers
                        size={22}
                        className="nav-item-icon"
                        style={{ marginLeft: "2px !important" }}
                      />
                    ),
                    text: "User Management",
                    redirect: () => {
                      dispatch(setUser({ nav: false }));
                      navigate("/user-management");
                    },
                  })
                : null}
            </Stack>

            <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.2)" }} />

            <Stack direction="row" justifyContent="space-between">
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ padding: "0px 12px", cursor: "pointer" }}
                onClick={() =>
                  window.open(
                    "https://share.hsforms.com/2-VPFzquMSROmDWhZbg3zPArmhfk",
                    "_blank"
                  )
                }
              >
                <HelpIcon />
                <Typography variant="body2">Help</Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ padding: "0px 12px", cursor: "pointer" }}
                onClick={logout}
              >
                <Typography variant="body2" fontWeight="bold">
                  Logout
                </Typography>
                <LogoutIcon />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </ClickAwayListener>
      {showConfirmation ? (
        <ConfirmationDialog
          open={showConfirmation}
          content={confirmationProps.content}
          title={confirmationProps.title}
          btn1={confirmationProps.btn1}
          btn2={confirmationProps.btn2}
          handleBtn1={confirmationProps.handleBtn1}
          handleBtn2={confirmationProps.handleBtn2}
          icon={confirmationProps.icon}
          actions={confirmationProps.actions}
          tooltip={confirmationProps.tooltip}
          bgcolor={confirmationProps.bgcolor}
        />
      ) : null}
    </>
  );
};

export default UserNav;
